import { Collection, Database, Q } from '@nozbe/watermelondb';

import HorseContact from './HorseContact';
import Organisation from './Organisation';
import {
    DBServiceOptionsWithImagesAndValidation,
    EntryDBServiceOptions,
    HorseDBServiceOptions,
} from '../../types/dbService';
import { HorseContactModel } from '../../types/HorseContact';
import { HorseModel, HorsePayload } from '../../types/Horses';
import { HorsesFiltersObject } from '../../types/horsesFilters';
import { copyClassInstance } from '../../utils/classes';
import Location from './Location';
import {
    applyHorsesFiltersQueries,
    getUnsafeHorsesFiltersQueries,
} from '../utils';
import { Queries } from '../../types/watermelonDb';
import { SortObject, UnsafeQuery } from '../../types/sort';
import { getDuplicatesFreeArrayByKey } from 'shared/utils/array';
import {
    addPreparedDataBatch,
    mapRecord,
    mapRecords,
    mapRecordsByTable,
    prepareHorseLocation,
    PreparedHorseData,
    mapEntryDeleteRecordsByTable,
} from './helpers/horse/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddHorseActions,
    prepareCreateHorse,
    prepareCreateHorseContacts,
} from './helpers/horse/horseAdd';
import {
    logUpdateHorseActions,
    prepareUpdateHorse,
    prepareUpdateHorseContacts,
} from './helpers/horse/horseUpdate';
import {
    logDeleteHorseActions,
    prepareDeleteEntries,
    prepareDeleteEventHorses,
    prepareDeleteHorse,
    prepareDeleteHorseContacts,
} from './helpers/horse/horseDelete';

import { createDefaultWriteStrategy } from '../operations/createDefaultWriteStrategy';
import { OperationContext } from '../../types/operations';
import { HorseWriteStrategy } from '../../types/writeStrategy';

class Horse {
    private database: Database;
    private collection: Collection<HorseModel>;
    private table = 'horses';
    private options: HorseDBServiceOptions;
    private writeStrategy: HorseWriteStrategy;

    constructor(options: HorseDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.writeStrategy =
            options.writeStrategy ?? createDefaultWriteStrategy();
    }

    countAll() {
        return this.collection.query().fetchCount();
    }

    observeCount() {
        return this.collection.query().observeCount();
    }

    /**
     * Live count of visible horses with neither a `locationId` nor a
     * `stableAddress`.
     *
     * Both columns need the empty-string arm as well as the null one.
     * `location_id` is declared non-optional, so WatermelonDB sanitizes a
     * missing value to `''` rather than null on every create, update and sync
     * pull — matching only null would never match a row. `stable_address` is
     * optional and so can hold either.
     *
     * `hidden` mirrors the `visible_only` filter the horses list itself runs
     * with, so the banner this count drives can't appear over a list with
     * nothing in it to act on.
     */
    observeCountWithoutLocation() {
        return this.collection
            .query(
                Q.where('hidden', false),
                Q.or(
                    Q.where('location_id', null),
                    Q.where('location_id', ''),
                ),
                Q.or(
                    Q.where('stable_address', null),
                    Q.where('stable_address', ''),
                ),
            )
            .observeCount();
    }

    getAll(...queries: Q.Clause[]) {
        return this.collection.query(...queries).fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    private filterHorses(
        searchText: string,
        filters?: HorsesFiltersObject,
        sort?: SortObject | null,
    ) {
        const parsedSearchText = searchText.toLowerCase().trim();
        const queries: Queries = [];

        if (parsedSearchText) {
            queries.push(
                Q.where(
                    'name',
                    Q.like(`${Q.sanitizeLikeString(parsedSearchText)}%`),
                ),
            );
        }

        if (filters) {
            queries.push(...applyHorsesFiltersQueries(filters));

            const unsafeQueries = getUnsafeHorsesFiltersQueries(filters);
            queries.push(
                //@ts-ignore
                Q.unsafeLokiTransform((raws, loki) => {
                    let result = raws;

                    //calculating unsafe filters and applying it into result
                    unsafeQueries.forEach(
                        (query: UnsafeQuery) => (result = query(result, loki)),
                    );

                    //applying unsafe sort logic over the updated result array
                    if (sort && sort.unsafeQuery)
                        result = sort.unsafeQuery(result, sort.value, loki);

                    return result;
                }),
            );
        }

        const sortQueries: Queries = [];
        if (sort && sort.query && sort.value) {
            const sortQuery = sort.query(sort.value);
            sortQueries.push(sortQuery);
        }

        return this.collection.query(...queries).extend(...sortQueries);
    }

    getFilteredHorsesCount(searchText: string, filters?: HorsesFiltersObject) {
        const query = this.filterHorses(searchText, filters);
        return query.fetchCount();
    }

    getFilteredHorses(
        searchText: string,
        sort: SortObject | null,
        filters?: HorsesFiltersObject,
    ) {
        const query = this.filterHorses(searchText, filters, sort);
        return query.fetch();
    }

    getBadgesNumber() {
        return this.collection
            .query(Q.where('shoeing_cycle_badge', true))
            .fetchCount();
    }

    async getIsRelatedToInvoice(horseId: string) {
        const result = await this.database.collections
            .get('entries')
            .query(
                Q.and(
                    Q.where('horse_id', horseId),
                    Q.and(
                        Q.where('invoice_id', Q.notEq('')),
                        Q.where('invoice_id', Q.notEq(null)),
                    ),
                ),
            )
            .fetchCount();

        return result > 0;
    }

    getHorsesByContactNameOrHorseName = async (searchText: string) => {
        // it could return duplicated horses - no idea why
        const result = await this.collection
            .query(
                Q.experimentalJoinTables(['horse_contacts']),
                Q.experimentalNestedJoin('horse_contacts', 'contacts'),
                Q.unsafeSqlQuery(
                    'select horses.* from horses ' +
                        'left join horse_contacts on horses.id is horse_contacts.horse_id ' +
                        'left join contacts on horse_contacts.contact_id is contacts.id ' +
                        `where (horses.name like '${Q.sanitizeLikeString(
                            searchText,
                        )}%' or horses.name like '% ${Q.sanitizeLikeString(
                            searchText,
                        )}%' or contacts.first_name || ' ' || ifnull(contacts.last_name, '') like '${Q.sanitizeLikeString(
                            searchText,
                        )}%' or contacts.first_name || ' ' || ifnull(contacts.last_name, '') like '% ${Q.sanitizeLikeString(
                            searchText,
                        )}%' or contacts.first_name like '${Q.sanitizeLikeString(
                            searchText,
                        )}%' or contacts.last_name like '${Q.sanitizeLikeString(
                            searchText,
                        )}%') and contacts._status is not 'deleted'
                        and horse_contacts._status is not 'deleted'
                        and horses._status is not 'deleted'`,
                ),
            )
            .fetch();

        const uniqueResult = getDuplicatesFreeArrayByKey(result, 'id');

        return uniqueResult;
    };

    async add(payload: HorsePayload, userId: string) {
        const { image, location } = payload;

        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        const newHorse = await this.database.write(async () => {
            let preparedData: PreparedHorseData[] = [];
            const { preparedLocation, existingLocation } =
                await prepareHorseLocation({
                    location,
                    userId,
                    organisationID,
                    database: this.database,
                });

            if (preparedLocation) {
                preparedData.push(
                    mapRecord(preparedLocation, 'locations', 'add'),
                );
            }

            const createdHorse = prepareCreateHorse({
                payload,
                userId,
                organisationID,
                locationElement: preparedLocation || existingLocation,
                collection: this.collection,
            });
            preparedData.push(mapRecord(createdHorse, 'horses', 'add'));

            const horseContactsToAdd = prepareCreateHorseContacts({
                contacts: payload.contacts,
                horse: createdHorse,
                database: this.database,
            });
            preparedData.push(
                ...mapRecords(horseContactsToAdd, 'horse_contacts', 'add'),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddHorseActions({
                horse: createdHorse,
                horseContactsToAdd,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return createdHorse;
        });

        if (image && image.uri) {
            // create a new entry in Firestore DB
            await this.options.imageService.uploadImage({
                image,
                entityID: newHorse.id,
                entityType: 'Horse',
                annotationImage: '',
                ownerID: userId,
                userIDs: [userId],
                organisationID,
            });
        }

        return newHorse;
    }

    async update(
        id: string,
        payload: Partial<HorsePayload>,
        userId: string,
    ): Promise<HorseModel> {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        const horseElement = await this.getByID(id);
        const { contacts, image, removeAvatar } = payload;
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        };
        const locationService = new Location(serviceOptions);
        const initialContacts = contacts
            ? await horseElement.contacts.fetch()
            : [];
        let horseContacts: HorseContactModel[] = [];

        if (contacts) {
            const horseContactService = new HorseContact(serviceOptions);
            horseContacts = await horseContactService.getByParam(
                'horse_id',
                horseElement.id,
            );
        }

        let fallbackLocationExists = false;

        if (horseElement.locationId) {
            const horseLocation = await locationService.getByParam(
                'id',
                horseElement.locationId,
            );
            fallbackLocationExists = horseLocation.length > 0;
        }

        const updatedHorseElement = await this.database.write(async () => {
            let preparedData: PreparedHorseData[] = [];
            const { preparedLocation, existingLocation } =
                await prepareHorseLocation({
                    location: payload.location,
                    userId,
                    organisationID,
                    database: this.database,
                });

            if (preparedLocation) {
                preparedData.push(
                    mapRecord(preparedLocation, 'locations', 'add'),
                );
            }

            const updatedHorse = prepareUpdateHorse({
                horseElement,
                payload,
                locationElement: preparedLocation || existingLocation,
                fallbackLocationExists,
                logError: this.options.logger.error,
                entityName: this.table,
            });
            preparedData.push(mapRecord(updatedHorse, 'horses', 'update'));

            const horseContactsCollection =
                this.database.collections.get<HorseContactModel>(
                    'horse_contacts',
                );
            const { horseContactsToAdd, horseContactsToDelete } =
                prepareUpdateHorseContacts({
                    contacts,
                    initialContacts,
                    horseContacts,
                    horseElement,
                    horseContactsCollection,
                });
            preparedData.push(
                ...mapRecords(horseContactsToAdd, 'horse_contacts', 'add'),
                ...mapRecords(
                    horseContactsToDelete,
                    'horse_contacts',
                    'delete',
                ),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateHorseActions({
                horse: updatedHorse,
                horseContactsToAdd,
                horseContactsToDelete,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return updatedHorse;
        });

        if (removeAvatar) {
            await this.options.imageService.remove(id);
        }

        if (image && image.uri) {
            await this.options.imageService.uploadImage({
                image,
                entityID: id,
                entityType: 'Horse',
                annotationImage: '',
                userIDs: [userId],
                documentID: image.documentID,
                organisationID,
            });
        }

        // It's not enough to return the updated element as the updated
        // element has the same reference as the one before the update.
        // So to make React able to detect a difference between the objects
        // (different references) we have to make a copy of this class instance.

        return copyClassInstance(updatedHorseElement);
    }

    async prepareBumpUpdates(ids: string[]) {
        const horseElements = await Promise.all(
            ids.map(async (id) => this.getByID(id)),
        );

        return horseElements.map((horseElement: HorseModel) =>
            horseElement.prepareUpdate((horse: HorseModel) => {
                horse.name = horseElement.name;
            }),
        );
    }

    async bump(ids: string[]) {
        await this.database.write(async () => {
            const updates = await this.prepareBumpUpdates(ids);

            const preparedData = mapRecords(updates, 'horses', 'update');

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);
        });
    }

    async deleteByID(
        id: string,
        userId: string,
        options?: { operationContext?: OperationContext },
    ) {
        const horseElement = await this.getByID(id);
        const contactHorses = await horseElement.contacts.fetch();
        const entries = await horseElement.entries.fetch();
        const events = await horseElement.events.fetch();
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        };
        const entryServiceOptions: EntryDBServiceOptions = {
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.options.validation,
        };

        let horseContactsToDelete: HorseContactModel[] = [];

        await this.database.write(async () => {
            let preparedData: PreparedHorseData[] = [];
            const deletedHorse = prepareDeleteHorse({ horse: horseElement });
            preparedData.push(mapRecord(deletedHorse, 'horses', 'delete'));

            horseContactsToDelete = await prepareDeleteHorseContacts({
                horseId: id,
                shouldDelete: contactHorses.length > 0,
                serviceOptions,
            });
            preparedData.push(
                ...mapRecords(
                    horseContactsToDelete,
                    'horse_contacts',
                    'delete',
                ),
            );

            const eventsHorseToDelete = await prepareDeleteEventHorses({
                horseId: id,
                shouldDelete: events.length > 0,
                serviceOptions,
            });
            preparedData.push(
                ...mapRecordsByTable(eventsHorseToDelete, 'delete'),
            );

            const entriesToDelete = await prepareDeleteEntries({
                entries,
                userId,
                serviceOptions: entryServiceOptions,
            });
            preparedData.push(...mapEntryDeleteRecordsByTable(entriesToDelete));

            validateEntityRecords(preparedData, this.options.validation);
            await this.writeStrategy.commitPreparedData({
                preparedData,
                database: this.database,
                transactionAction: 'delete',
                operationContext: options?.operationContext,
            });
            logDeleteHorseActions({
                horse: deletedHorse,
                horseContactsToDelete,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });

        this.options.imageService.remove(id);
    }
}

export default Horse;
